import React, { useEffect } from "react";
import { DocumentNode } from "graphql";
import { useMutation } from "@apollo/client";
import { dispatchEventBus } from "@catchoftheday/state";
import {
  createToast,
  updateToast,
  ItemProps,
  ToastContentProps,
} from "@catchoftheday/header-app";
import {
  AddToCartDocument,
  RemoveItemFromCartDocument,
  MoveItemFromCartToWishlistDocument,
  Mutation,
} from "@cart-app/generated/graphql";
import {
  UNABLE_TO_ADD_TO_CART,
  UNABLE_TO_REMOVE_ITEM,
  UNABLE_TO_ADD_TO_WISHLIST,
  GENERIC_ERROR,
} from "@cart-app/consts";

export enum MutationType {
  ADD_TO_CART = "addToCart",
  REMOVE_ITEM_FROM_CART = "removeItemFromCart",
  MOVE_ITEM_FROM_CART_TO_WISHLIST = "moveItemFromCartToWishlist",
}

type ToastConfig = {
  mutationDocumentNode: DocumentNode;
  showCheckoutOnSuccess?: boolean;
  showTickOnSuccess?: boolean;
  eventBusMessageType?: string;
  messages: {
    loading: string;
    success: string;
    error: string;
  };
};

type UseToastProps = {
  mutationType: MutationType;
  setError?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  showCheckoutOnSuccess?: boolean;
  showTickOnSuccess?: boolean;
};
type MutateToastProps = {
  imageUrl: string;
  title: string;
};

const toastConfig: Record<MutationType, ToastConfig> = {
  addToCart: {
    mutationDocumentNode: AddToCartDocument,
    showCheckoutOnSuccess: true,
    eventBusMessageType: "@cart-app/ADD_TO_CART",
    messages: {
      loading: "Adding...",
      success: "Added!",
      error: UNABLE_TO_ADD_TO_CART,
    },
  },
  removeItemFromCart: {
    mutationDocumentNode: RemoveItemFromCartDocument,
    showTickOnSuccess: true,
    eventBusMessageType: "@cart-app/REMOVE_ITEM_FROM_CART",
    messages: {
      loading: "Removing...",
      success: "Item removed from cart.",
      error: UNABLE_TO_REMOVE_ITEM,
    },
  },
  moveItemFromCartToWishlist: {
    mutationDocumentNode: MoveItemFromCartToWishlistDocument,
    showTickOnSuccess: true,
    eventBusMessageType: "@cart-app/ADD_TO_WISHLIST",
    messages: {
      loading: "Adding to wishlist!",
      success: "Added to wishlist!",
      error: UNABLE_TO_ADD_TO_WISHLIST,
    },
  },
};

const getResponse = (
  mutationType: MutationType,
  data: Mutation | null | undefined
) => {
  if (!data) {
    return null;
  }
  return data?.[mutationType];
};

export const useToast = <T>({
  mutationType,
  setError,
  setLoading,
  showCheckoutOnSuccess,
  showTickOnSuccess,
}: UseToastProps) => {
  const {
    mutationDocumentNode,
    showCheckoutOnSuccess: defaultShowCheckoutOnSuccess,
    showTickOnSuccess: defaultShowTickOnSuccess,
    eventBusMessageType,
    messages: {
      loading: loadingMessage,
      success: successMessage,
      error: generalErrorMessage,
    },
  } = toastConfig[mutationType];

  const [mutateFunction, { data, error, loading }] = useMutation<Mutation, T>(
    mutationDocumentNode
  );
  const mutationResponse = getResponse(mutationType, data);

  const returnMutateFunction = async (
    { imageUrl, title }: MutateToastProps,
    mutateVariables: T
  ) => {
    const toastItem: ItemProps = {
      message: loadingMessage,
      status: "info",
      imageUrl,
      title,
      showTickOnSuccess:
        typeof showTickOnSuccess === "boolean"
          ? showTickOnSuccess
          : defaultShowTickOnSuccess,
    };
    const toastContent: ToastContentProps = {
      items: [toastItem],
      showCheckoutOnSuccess:
        typeof showCheckoutOnSuccess === "boolean"
          ? showCheckoutOnSuccess
          : defaultShowCheckoutOnSuccess,
    };
    const toastId = createToast({ toastContent });

    const { data, errors = [] } = await mutateFunction({
      variables: mutateVariables,
    });

    const mutateResponse = getResponse(mutationType, data);

    if (errors.length > 0) {
      updateToast({
        toastId,
        toastContent: {
          ...toastContent,
          items: [
            { ...toastItem, message: generalErrorMessage, status: "error" },
          ],
        },
      });
      return { status: "ServerError" };
    }
    if (mutateResponse?.__typename === "UserError") {
      updateToast({
        toastId,
        toastContent: {
          ...toastContent,
          items: [
            { ...toastItem, message: mutateResponse?.message, status: "error" },
          ],
        },
      });
      return { status: "UserError" };
    }
    if (mutateResponse?.__typename === "Cart") {
      updateToast({
        toastId,
        toastContent: {
          ...toastContent,
          items: [{ ...toastItem, message: successMessage, status: "success" }],
        },
      });
      if (eventBusMessageType) {
        dispatchEventBus({
          type: eventBusMessageType,
        });
      }
    }
    return { status: "OK" };
  };

  useEffect(() => {
    const userError =
      mutationResponse?.__typename === "UserError"
        ? mutationResponse.message
        : undefined;

    const errorMessage = error ? GENERIC_ERROR : userError;
    if (errorMessage && setError) {
      setError(errorMessage);
    }
  }, [mutationResponse, error, setError]);

  useEffect(() => {
    if (setLoading) {
      setLoading(loading);
    }
  }, [loading, setLoading]);

  return returnMutateFunction;
};
